import { useParams } from 'react-router-dom';

import { graphql } from '@e-socna/data-layer';

import {
  PATH_FOLDER_ADDRESS,
  PATH_FOLDER_ADDRESS_EDIT,
  PATH_FOLDER_ARCHIVE_INFO,
  PATH_FOLDER_EXTERNAL_ACTORS,
  PATH_FOLDER_EXTERNAL_ACTORS_EDIT,
  PATH_FOLDER_EXTERNAL_ACTORS_EDIT_DELETE,
  PATH_FOLDER_EXTERNAL_ACTORS_EDIT_FLUSH,
  PATH_FOLDER_EXTERNAL_ACTORS_EDIT_CUSTOMIZE,
  PATH_FOLDER_EXTERNAL_ACTORS_EDIT_MODIFY,
  PATH_FOLDER_EXTERNAL_ACTORS_EDIT_NEW,
  PATH_FOLDER_GENERAL,
  PATH_FOLDER_GENERAL_EDIT,
  PATH_FOLDER_GENERAL_EDIT_CODE,
  PATH_FOLDER_GENERAL_EDIT_STATE,
  PATH_FOLDER_INTERNAL_ACTORS,
  PATH_FOLDER_INTERNAL_ACTORS_EDIT,
  PATH_FOLDER_INTERNAL_ACTORS_EDIT_DELETE,
  PATH_FOLDER_INTERNAL_ACTORS_EDIT_FLUSH,
  PATH_FOLDER_INTERNAL_ACTORS_EDIT_CUSTOMIZE,
  PATH_FOLDER_INTERNAL_ACTORS_EDIT_MODIFY,
  PATH_FOLDER_INTERNAL_ACTORS_EDIT_NEW,
  PATH_FOLDER_VERSIONS,
  PATH_FOLDER_DEFAULT,
} from '../../app/constants/paths.constant';
import { Tabs, TabsStrategy } from '../../components/Tabs';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';
import { useFolderReadyContext } from '../../contexts/FolderContext/hooks/useFolderReadyContext.hook';

import { Address } from './Address';
import { Actors } from './Actors';
import { ArchiveInfo } from './ArchiveInfo';
import { GeneralInfo } from './GeneralInfo';
import { Versions } from './Versions';

export const FolderPage_Tabs_Folder_Fragment = graphql(`
  fragment FolderPage_Tabs_Folder_Fragment on Folder {
    _id
    ...FolderPage_Address_Folder_Fragment
    ...FolderPage_Actors_Folder_Fragment
    ...FolderPage_ArchiveInfo_Folder_Fragment
    ...FolderPage_GeneralInfo_Folder_Fragment
  }
`);

export const FolderTabs = () => {
  const { folderId } = useFolderReadyContext();
  const { actorId } = useParams();

  const { paths, getActiveTab } = useBuildPaths(
    {
      address: PATH_FOLDER_ADDRESS,
      addressEdit: PATH_FOLDER_ADDRESS_EDIT,
      archiveInfo: PATH_FOLDER_ARCHIVE_INFO,
      default: PATH_FOLDER_DEFAULT,
      externalActors: PATH_FOLDER_EXTERNAL_ACTORS,
      externalActorsEdit: PATH_FOLDER_EXTERNAL_ACTORS_EDIT,
      externalActorsEditCustomize: PATH_FOLDER_EXTERNAL_ACTORS_EDIT_CUSTOMIZE,
      externalActorsEditDelete: PATH_FOLDER_EXTERNAL_ACTORS_EDIT_DELETE,
      externalActorsEditFlush: PATH_FOLDER_EXTERNAL_ACTORS_EDIT_FLUSH,
      externalActorsEditModify: PATH_FOLDER_EXTERNAL_ACTORS_EDIT_MODIFY,
      externalActorsEditNew: PATH_FOLDER_EXTERNAL_ACTORS_EDIT_NEW,
      generalInfo: PATH_FOLDER_GENERAL,
      generalInfoEdit: PATH_FOLDER_GENERAL_EDIT,
      generalInfoEditCode: PATH_FOLDER_GENERAL_EDIT_CODE,
      generalInfoEditState: PATH_FOLDER_GENERAL_EDIT_STATE,
      internalActors: PATH_FOLDER_INTERNAL_ACTORS,
      internalActorsEdit: PATH_FOLDER_INTERNAL_ACTORS_EDIT,
      internalActorsEditCustomize: PATH_FOLDER_INTERNAL_ACTORS_EDIT_CUSTOMIZE,
      internalActorsEditDelete: PATH_FOLDER_INTERNAL_ACTORS_EDIT_DELETE,
      internalActorsEditFlush: PATH_FOLDER_INTERNAL_ACTORS_EDIT_FLUSH,
      internalActorsEditModify: PATH_FOLDER_INTERNAL_ACTORS_EDIT_MODIFY,
      internalActorsEditNew: PATH_FOLDER_INTERNAL_ACTORS_EDIT_NEW,
      versions: PATH_FOLDER_VERSIONS,
    },
    {
      folderId,
      actorId,
    },
    {
      address: 'address',
      addressEdit: 'address',
      archiveInfo: 'archiveInfo',
      default: 'generalInfo',
      externalActors: 'externalActors',
      externalActorsEdit: 'externalActors',
      externalActorsEditCustomize: 'externalActors',
      externalActorsEditDelete: 'externalActors',
      externalActorsEditFlush: 'externalActors',
      externalActorsEditModify: 'externalActors',
      externalActorsEditNew: 'externalActors',
      generalInfo: 'generalInfo',
      generalInfoEdit: 'generalInfo',
      generalInfoEditCode: 'generalInfo',
      generalInfoEditState: 'generalInfo',
      internalActors: 'internalActors',
      internalActorsEdit: 'internalActors',
      internalActorsEditCustomize: 'internalActors',
      internalActorsEditDelete: 'internalActors',
      internalActorsEditFlush: 'internalActors',
      internalActorsEditModify: 'internalActors',
      internalActorsEditNew: 'internalActors',
      versions: 'versions',
    },
  );

  const generalInfoPaths = {
    consult: paths.generalInfo,
    editGeneralInfo: paths.generalInfoEdit,
    editCode: paths.generalInfoEditCode,
    editState: paths.generalInfoEditState,
  } as const;

  const addressPaths = {
    consult: paths.address,
    edit: paths.addressEdit,
  } as const;

  const internalActorsPaths = {
    consult: paths.internalActors,
    edit: paths.internalActorsEdit,
    editDelete: paths.internalActorsEditDelete,
    editFlush: paths.internalActorsEditFlush,
    editCustomize: paths.internalActorsEditCustomize,
    editModify: paths.internalActorsEditModify,
    editNew: paths.internalActorsEditNew,
  } as const;

  const externalActorsPaths = {
    consult: paths.externalActors,
    edit: paths.externalActorsEdit,
    editDelete: paths.externalActorsEditDelete,
    editFlush: paths.externalActorsEditFlush,
    editCustomize: paths.externalActorsEditCustomize,
    editModify: paths.externalActorsEditModify,
    editNew: paths.externalActorsEditNew,
  } as const;

  return (
    <Tabs
      activeTab={getActiveTab()}
      id="folder-tabs"
      strategy={TabsStrategy.Controlled}
      tabs={[
        {
          key: 'generalInfo',
          label: 'Informations Générales',
          panel: <GeneralInfo paths={generalInfoPaths} />,
          to: paths.generalInfo,
          value: 'generalInfo',
        },
        {
          key: 'address',
          label: 'Adresse',
          panel: <Address paths={addressPaths} />,
          to: paths.address,
          value: 'address',
        },
        {
          key: 'internalActors',
          label: 'Acteurs SOCNA',
          panel: <Actors isInternal paths={internalActorsPaths} />,
          to: paths.internalActors,
          value: 'internalActors',
        },
        {
          key: 'externalActors',
          label: 'Acteurs Externes',
          panel: <Actors isInternal={false} paths={externalActorsPaths} />,
          to: paths.externalActors,
          value: 'externalActors',
        },
        {
          key: 'archiveInfo',
          label: 'Archivage',
          panel: <ArchiveInfo />,
          to: paths.archiveInfo,
          value: 'archiveInfo',
        },
        {
          key: 'versions',
          label: 'Versions',
          panel: <Versions />,
          to: paths.versions,
          value: 'versions',
        },
      ]}
      title="Onglets de l'écran de Fiche d'un Dossier"
    />
  );
};
